import { useApp } from '@/contexts';

import { useEffect, useState } from 'react';

import {
  Alert,
  Box,
  CircularProgress,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useSearchParams } from 'react-router';

import { useAppDispatch } from '@/app/hooks';
import { PageContainer } from '@/components/layout';

import { transformersApi, useGetStepLogsQuery } from '../api/transformersApi';

/**
 * Salesforce Bulk Jobs Info page
 * Shows details about Salesforce bulk job operations from execution step extended properties
 * Dynamically renders all columns returned by the API (matching dataProtect behavior)
 *
 * URL: /a/:appId/health/sfjobs?step=xxx&env=xxx
 */
export const SfJobsPage = () => {
  const { appId, tenantId } = useApp();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const stepGuid = searchParams.get('step');
  const envId = searchParams.get('env');

  const [jobs, setJobs] = useState<Record<string, unknown>[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionKey, setConnectionKey] = useState<string>('defaultConnection');

  // Fetch step logs to get extended properties
  const {
    data: stepData,
    isLoading: isLoadingStep,
    error: stepError,
  } = useGetStepLogsQuery(
    { tenantId: String(tenantId), stepGuid: stepGuid! },
    { skip: !tenantId || !stepGuid }
  );

  // When step data is loaded, parse job IDs and fetch job info
  useEffect(() => {
    const fetchJobsInfo = async () => {
      if (!stepData?.extendedProperties || !tenantId || !appId || !envId) {
        return;
      }

      setIsLoadingJobs(true);
      setError(null);

      try {
        const extendedProps = stepData.extendedProperties;
        if (!extendedProps || extendedProps === '') {
          setJobs([]);
          return;
        }

        // Parse job IDs from extended properties
        const jobsData = JSON.parse(extendedProps) as Array<{ sf__jobId: string }>;
        const jobIds = jobsData.map((j) => j.sf__jobId).filter(Boolean);

        if (jobIds.length === 0) {
          setJobs([]);
          return;
        }

        // Determine connection key (default to 'defaultConnection')
        const connKey = stepData.connectionKey || 'defaultConnection';
        setConnectionKey(connKey);

        // Fetch info for each job
        const jobInfoPromises = jobIds.map(async (jobId) => {
          try {
            const result = await dispatch(
              transformersApi.endpoints.getSfJobInfo.initiate(
                {
                  tenantId: String(tenantId),
                  appId: String(appId),
                  envId: String(envId),
                  connectionKey: connKey,
                  jobId,
                },
                { forceRefetch: true }
              )
            ).unwrap();

            // Create a mutable copy (RTK Query returns frozen objects)
            const jobData: Record<string, unknown> = { ...result };

            // Format the created date
            if (jobData['Created Date']) {
              jobData['Created Date'] = format(
                new Date(jobData['Created Date'] as string),
                'MMM do, h:mm:ss a'
              );
            }

            // Store the job ID for linking to failed/success results
            jobData['_jobId'] = jobId;

            return jobData;
          } catch (err) {
            console.error(`Error fetching job info for ${jobId}:`, err);
            return null;
          }
        });

        const results = await Promise.all(jobInfoPromises);
        const validJobs = results.filter((j): j is Record<string, unknown> => j !== null);

        // Sort by created date
        validJobs.sort((a, b) => {
          const dateA = new Date(String(a['Created Date'] || '')).getTime();
          const dateB = new Date(String(b['Created Date'] || '')).getTime();
          return dateA - dateB;
        });

        // Extract headers dynamically from all jobs (matching dataProtect behavior)
        // Remove "Number " prefix from headers like "Number Records Processed" → "Records Processed"
        const headerSet = new Set<string>();
        validJobs.forEach((job) => {
          Object.keys(job).forEach((key) => {
            if (key !== '_jobId') {
              const displayKey = key.replace('Number ', '');
              headerSet.add(displayKey);
            }
          });
        });

        setHeaders(Array.from(headerSet));
        setJobs(validJobs);
      } catch (err) {
        console.error('Error parsing extended properties:', err);
        setError('Failed to parse job information');
      } finally {
        setIsLoadingJobs(false);
      }
    };

    fetchJobsInfo();
  }, [stepData, tenantId, appId, envId, dispatch]);

  if (!stepGuid || !envId) {
    return (
      <PageContainer>
        <Typography variant="h6" sx={{ fontSize: 14, fontWeight: 600, mb: 2 }}>
          Salesforce Bulk Jobs Info
        </Typography>
        <Alert severity="warning">
          Missing required parameters. Please access this page from an execution details view.
        </Alert>
      </PageContainer>
    );
  }

  if (stepError) {
    return (
      <PageContainer>
        <Typography variant="h6" sx={{ fontSize: 14, fontWeight: 600, mb: 2 }}>
          Salesforce Bulk Jobs Info
        </Typography>
        <Alert severity="error">Failed to load step information.</Alert>
      </PageContainer>
    );
  }

  const isLoading = isLoadingStep || isLoadingJobs;

  // Get cell value, handling the "Number " prefix in API response
  const getCellValue = (job: Record<string, unknown>, header: string): unknown => {
    // Try direct match first
    if (header in job) return job[header];
    // Try with "Number " prefix
    if (`Number ${header}` in job) return job[`Number ${header}`];
    return '';
  };

  // Check if a cell should be clickable (Records Succeeded/Failed with value > 0)
  const isClickableCell = (header: string, value: unknown): boolean => {
    const numValue = Number(value);
    return (
      (header === 'Records Succeeded' || header === 'Records Failed') &&
      !isNaN(numValue) &&
      numValue > 0
    );
  };

  // Get link URL for clickable cells
  const getCellLink = (job: Record<string, unknown>, header: string): string => {
    const jobId = job['_jobId'] || job['Job Id'];
    const type = header === 'Records Failed' ? 'failed' : 'successful';
    return `/a/${appId}/health/sfjobs/${type}?jobId=${jobId}&env=${envId}&connectionKey=${connectionKey}`;
  };

  return (
    <PageContainer>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: '#333',
          }}
        >
          Salesforce Bulk Jobs Info
        </Typography>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading job information...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : jobs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No data found.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'auto' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {headers.map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      fontSize: 13,
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job, index) => (
                <TableRow key={String(job['Job Id']) || index}>
                  {headers.map((header) => {
                    const value = getCellValue(job, header);
                    const clickable = isClickableCell(header, value);

                    return (
                      <TableCell
                        key={header}
                        sx={{
                          fontSize: 13,
                          whiteSpace: 'nowrap',
                          fontFamily: header === 'Job Id' ? 'monospace' : 'inherit',
                        }}
                      >
                        {clickable ? (
                          <MuiLink
                            href={getCellLink(job, header)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: 'primary.main',
                              cursor: 'pointer',
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {String(value)}
                          </MuiLink>
                        ) : (
                          String(value ?? '')
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </PageContainer>
  );
};
